import { memo, useMemo } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button, Dropdown, type DropdownItem } from '@cyphex/ui';
import type {
  AudioProcessingPreferences, DenoiseRuntime, NoiseSuppressionMode,
} from '../../media/settings/preferences';
import { SettingRow, ToggleRow } from './SettingRow';

export interface AudioTabProps {
  audio: AudioProcessingPreferences;
  /** What the media plane actually ended up running. */
  runtime: DenoiseRuntime;
  onChange: (patch: Partial<AudioProcessingPreferences>) => void;
}

const MODE_LABEL: Readonly<Record<NoiseSuppressionMode, string>> = {
  off: 'Off',
  native: 'Standard (browser)',
  rnnoise: 'High (RNNoise)',
};

const MODE_ORDER: readonly NoiseSuppressionMode[] = ['rnnoise', 'native', 'off'];

const RUNTIME_NOTICE: Readonly<Record<DenoiseRuntime, string | null>> = {
  rnnoise: null,
  native: null,
  off: null,
  unavailable:
    'High-quality suppression could not load on this device — the browser’s standard suppression is running instead.',
};

/**
 * Noise suppression, echo cancellation and auto gain. All three run in the
 * BROWSER: RNNoise as an AudioWorklet in the publish path, the other two as
 * native WebRTC constraints on the capture. The server sees a finished stream.
 */
export const AudioTab = memo(function AudioTab({ audio, runtime, onChange }: AudioTabProps) {
  const items = useMemo<readonly DropdownItem[]>(
    () =>
      MODE_ORDER.map((mode) => ({
        id: mode,
        label: MODE_LABEL[mode],
        checked: audio.noiseSuppression === mode,
        onSelect: () => onChange({ noiseSuppression: mode }),
      })),
    [audio.noiseSuppression, onChange],
  );

  return (
    <div className="flex flex-col divide-y divide-border">
      <SettingRow
        label="Noise suppression"
        hint="High mode removes keyboards, fans and traffic before your audio leaves this device."
        notice={RUNTIME_NOTICE[runtime]}
      >
        <Dropdown
          align="end"
          items={items}
          label="Noise suppression mode"
          trigger={
            <Button variant="outline-neutral" size="sm" rightIcon={ChevronDown}>
              {MODE_LABEL[audio.noiseSuppression]}
            </Button>
          }
        />
      </SettingRow>

      <ToggleRow
        label="Echo cancellation"
        hint="Stops other people hearing themselves back through your speakers."
        checked={audio.echoCancellation}
        onChange={(echoCancellation) => onChange({ echoCancellation })}
      />

      <ToggleRow
        label="Automatic gain"
        hint="Evens out your level when you lean away from the microphone."
        checked={audio.autoGainControl}
        onChange={(autoGainControl) => onChange({ autoGainControl })}
      />
    </div>
  );
});
